import { Block } from '~/components/AIInsightsUI/blockTypes';
import {
  ConversationMessage,
  ConversationRole,
  MessageCitation as GqlMessageCitation,
  MessageSource as GqlMessageSource,
} from '~/types/api.graphql';

import { extractCitationText } from '../AIInsightsSidebar/WebSearch/citationUtils';
import { MessageCitation, MessageSource, ThinkingSource } from '../AIInsightsSidebar/WebSearch/webSearch.types';

export type { MessageCitation, MessageSource, ThinkingSource };

export type PdfReport = {
  downloadUrl?: string;
  expiresAt?: string;
  relativePath: string;
};

export type MessagePart =
  | { type: 'text'; text: string }
  | { type: 'block'; block: Block }
  | { type: 'pdfReport'; report: PdfReport };

export type UIMessage = ConversationMessage & {
  parts?: MessagePart[];
  citations?: MessageCitation[];
  sources?: MessageSource[];
};

// =============================================================================
// Enums
// =============================================================================

export enum ConversationEntityType {
  Audience = 'audience',
}

// =============================================================================
// Constants
// =============================================================================

export const ERROR_MESSAGE_PREFIX = 'error-';
export const MESSAGES_PER_PAGE = 20;
export const SCROLL_THRESHOLD = 100;
export const USAGE_QUERY_DELAY = 10_000;

// =============================================================================
// String helpers
// =============================================================================

// Remove leading newlines to avoid initial padding in messages
export const stripLeadingNewlines = (text: string): string => text.replace(/^\n+/, '');

// =============================================================================
// Date helpers
// =============================================================================

export const formatMessageTimestamp = (messageId: string): string | null => {
  // Extract timestamp from message IDs created locally (format: prefix-timestamp)
  const parts = messageId.split('-');
  const timestamp = parseInt(parts[parts.length - 1], 10);

  if (isNaN(timestamp) || timestamp < 1000000000000) return null;

  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// =============================================================================
// Message helpers
// =============================================================================

export const removeLastErrorMessage = (messages: UIMessage[]): UIMessage[] => {
  const lastMessage = messages[messages.length - 1];
  const isError = lastMessage?.id.startsWith(ERROR_MESSAGE_PREFIX) && lastMessage?.role === ConversationRole.Assistant;
  return isError ? messages.slice(0, -1) : messages;
};

export type ApiMessageWithSources = ConversationMessage & {
  citations?: GqlMessageCitation[] | null;
  sources?: GqlMessageSource[] | null;
};

// Maps a persisted API message to a UIMessage, assembling an ordered `parts` array.
// A message is at most one text body followed by one chart followed by pdf reports (the BE never
// emits text-after-chart or multiple charts in a single message), so [text?, block?, ...pdfReport?]
// reconstructs it faithfully and reloaded conversations match what was streamed live.
export const mapApiMessageToUIMessage = ({
  block,
  citations,
  sources,
  pdfReports,
  ...message
}: ApiMessageWithSources): UIMessage => {
  const parts: MessagePart[] = [];
  if (message.body) {
    parts.push({ type: 'text', text: message.body });
  }
  if (block) {
    parts.push({ type: 'block', block: block as Block });
  }
  (pdfReports ?? []).forEach((report) =>
    parts.push({ type: 'pdfReport', report: { relativePath: report.relativePath } }),
  );
  return {
    ...message,
    parts: parts.length > 0 ? parts : undefined,
    citations: (citations ?? []).map(
      (c, i): MessageCitation => ({
        title: c.title,
        url: c.url ?? undefined,
        text: extractCitationText(c.text),
        start: c.start ?? 0,
        end: c.end ?? 0,
        sourceNumber: i + 1,
      }),
    ),
    sources: (sources ?? []).map((s): MessageSource => ({ title: s.title, url: s.url })),
  };
};

export const createUserMessage = (body: string): UIMessage =>
  ({ id: `user-${Date.now()}`, body, role: ConversationRole.User } as UIMessage);

// `parts` drives the sidebar's ordered text/chart rendering. `body` is kept as the joined text of
// all text parts so consumers that only render `message.body` (e.g. AIInsightsModal) keep working.
export const createAssistantMessage = (
  parts: MessagePart[],
  citations: MessageCitation[] = [],
  sources: MessageSource[] = [],
): UIMessage =>
  ({
    id: `assistant-${Date.now()}`,
    role: ConversationRole.Assistant,
    parts,
    citations,
    sources,
    body: parts
      .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text')
      .map((part) => part.text)
      .join('\n'),
  } as UIMessage);

export const createErrorMessage = (body: string): UIMessage =>
  ({ id: `${ERROR_MESSAGE_PREFIX}${Date.now()}`, body, role: ConversationRole.Assistant } as UIMessage);

// =============================================================================
// Deeplink helpers
// =============================================================================

// Converts the current browser path to an amp:// deeplink.
// If an entity deeplink is provided (e.g. amp://audiences/71?name=Acme), any sub-path beyond
// the entity base (e.g. /lookalikes) is inserted before the query string.
// For non-entity pages, strips /app/ and prefixes with amp:// (e.g. /app/dashboard → amp://dashboard).
export const computeCurrentLocationDeeplink = (entityDeepLink: string | null, pathname: string): string => {
  const ampProtocol = 'amp://';
  if (!entityDeepLink || !entityDeepLink.startsWith(ampProtocol)) {
    const withoutApp = pathname.replace(/^\/app(\/|$)/, '');
    return `${ampProtocol}${withoutApp}`;
  }

  const withoutProtocol = entityDeepLink.slice(ampProtocol.length);
  const queryStart = withoutProtocol.indexOf('?');
  const pathPart = queryStart >= 0 ? withoutProtocol.slice(0, queryStart) : withoutProtocol;
  const queryString = queryStart >= 0 ? withoutProtocol.slice(queryStart + 1) : '';
  const entityBasePath = `/app/${pathPart}`;

  if (pathname !== entityBasePath && !pathname.startsWith(`${entityBasePath}/`)) {
    const withoutApp = pathname.replace(/^\/app(\/|$)/, '');
    return `${ampProtocol}${withoutApp}`;
  }

  const extraPath = pathname.slice(entityBasePath.length);
  const newPath = `${ampProtocol}${pathPart}${extraPath}`;
  return queryString ? `${newPath}?${queryString}` : newPath;
};
